import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Edit3, X, Calendar as CalendarIcon, FileSearch, Briefcase, Mail, LayoutDashboard, Settings as SettingsIcon, FileText, Upload, Image as ImageIcon } from "lucide-react";
import { api, LOGO_URL, formatApiError, uploadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DEFAULT_CONTENT } from "@/contexts/ContentContext";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "content", label: "Content Manager", icon: FileText },
  { key: "site-settings", label: "Site Settings", icon: SettingsIcon },
  { key: "appointments", label: "Appointments", icon: CalendarIcon, endpoint: "/appointments" },
  { key: "second-opinions", label: "Second Opinions", icon: FileSearch, endpoint: "/second-opinions" },
  { key: "career-applications", label: "Career Applications", icon: Briefcase, endpoint: "/career-applications" },
  { key: "contact-messages", label: "Contact Messages", icon: Mail, endpoint: "/contact-messages" },
  { key: "doctors", label: "Doctors", endpoint: "/doctors",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "qualification", label: "Qualification", required: true },
      { name: "specialty", label: "Specialty", required: true },
      { name: "department", label: "Department", required: true },
      { name: "experience_years", label: "Experience (years)", type: "number", required: true },
      { name: "languages", label: "Languages (comma-separated)", type: "list" },
      { name: "timings", label: "Timings" },
      { name: "profile", label: "Profile", type: "textarea" },
      { name: "photo", label: "Photo URL" },
      { name: "education", label: "Education (comma-separated)", type: "list" },
      { name: "expertise", label: "Expertise (comma-separated)", type: "list" },
      { name: "procedures", label: "Procedures (comma-separated)", type: "list" },
      { name: "awards", label: "Awards (comma-separated)", type: "list" },
      { name: "featured", label: "Featured", type: "checkbox" },
    ],
    cols: ["name", "specialty", "experience_years"],
  },
  { key: "specialties", label: "Specialties", endpoint: "/specialties",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "slug", label: "Slug (url-friendly)", required: true },
      { name: "icon", label: "Icon name", placeholder: "Stethoscope" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "image", label: "Image URL" },
    ],
    cols: ["name", "slug"],
  },
  { key: "services", label: "Services", endpoint: "/services",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "icon", label: "Icon name" },
      { name: "image", label: "Image URL" },
    ],
    cols: ["name"],
  },
  { key: "packages", label: "Packages", endpoint: "/packages",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "category", label: "Category", required: true },
      { name: "price", label: "Price (INR)", type: "number", required: true },
      { name: "tests", label: "Tests (comma-separated)", type: "list" },
      { name: "description", label: "Description", type: "textarea" },
    ],
    cols: ["name", "category", "price"],
  },
  { key: "insurance", label: "Insurance", endpoint: "/insurance",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "category", label: "Category (cashless / corporate / govt / tpa)", required: true },
      { name: "logo", label: "Logo URL" },
    ],
    cols: ["name", "category"],
  },
  { key: "schemes", label: "Govt Schemes", endpoint: "/schemes",
    fields: [
      { name: "name", label: "Name", required: true },
      { name: "eligibility", label: "Eligibility" },
      { name: "benefits", label: "Benefits" },
      { name: "documents", label: "Documents (comma-separated)", type: "list" },
      { name: "contact", label: "Contact" },
    ],
    cols: ["name", "contact"],
  },
  { key: "testimonials", label: "Testimonials", endpoint: "/testimonials",
    fields: [
      { name: "patient_name", label: "Patient name", required: true },
      { name: "rating", label: "Rating (1-5)", type: "number", required: true },
      { name: "review", label: "Review", type: "textarea", required: true },
      { name: "treatment", label: "Treatment" },
      { name: "photo", label: "Photo URL" },
    ],
    cols: ["patient_name", "treatment", "rating"],
  },
  { key: "articles", label: "Articles", endpoint: "/articles",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "category", label: "Category" },
      { name: "summary", label: "Summary", type: "textarea" },
      { name: "content", label: "Content", type: "textarea" },
      { name: "image", label: "Image URL" },
    ],
    cols: ["title", "category"],
  },
  { key: "faqs", label: "FAQs", endpoint: "/faqs",
    fields: [
      { name: "question", label: "Question", required: true },
      { name: "answer", label: "Answer", type: "textarea", required: true },
    ],
    cols: ["question"],
  },
  { key: "careers", label: "Job Openings", endpoint: "/careers",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "department", label: "Department", required: true },
      { name: "location", label: "Location" },
      { name: "type", label: "Type (Full-time / Part-time)" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "requirements", label: "Requirements (comma-separated)", type: "list" },
    ],
    cols: ["title", "department", "type"],
  },
  { key: "gallery", label: "Gallery", endpoint: "/gallery",
    fields: [
      { name: "title", label: "Title", required: true },
      { name: "category", label: "Category", required: true },
      { name: "image", label: "Image URL", required: true },
    ],
    cols: ["title", "category"],
  },
];

function fieldsToForm(fields) {
  const f = {};
  fields.forEach((x) => { f[x.name] = x.type === "list" ? "" : x.type === "checkbox" ? false : x.type === "number" ? "" : ""; });
  return f;
}

function CrudPanel({ tab }) {
  const { endpoint, fields, cols } = tab;
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(fieldsToForm(fields));
  const [open, setOpen] = useState(false);

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openCreate = () => { setEditing(null); setForm(fieldsToForm(fields)); setOpen(true); };
  const openEdit = (item) => {
    const f = {};
    fields.forEach((x) => {
      if (x.type === "list") f[x.name] = (item[x.name] || []).join(", ");
      else if (x.type === "checkbox") f[x.name] = !!item[x.name];
      else f[x.name] = item[x.name] ?? "";
    });
    setForm(f); setEditing(item); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      fields.forEach((x) => {
        const v = form[x.name];
        if (x.type === "list") payload[x.name] = String(v).split(",").map((s) => s.trim()).filter(Boolean);
        else if (x.type === "number") payload[x.name] = Number(v);
        else if (x.type === "checkbox") payload[x.name] = !!v;
        else payload[x.name] = v;
      });
      if (editing) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success(editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`${endpoint}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid={`panel-${tab.key}`}>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-bold">{tab.label}</h2>
        <button onClick={openCreate} className="btn-primary !py-2 !px-4 text-sm" data-testid={`add-${tab.key}`}><Plus className="h-4 w-4" /> Add new</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>{cols.map((c) => <th key={c} className="px-4 py-3">{c.replace(/_/g, " ")}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`row-${tab.key}-${it.id}`}>
                {cols.map((c) => <td key={c} className="px-4 py-3">{String(it[c] ?? "—").slice(0, 80)}</td>)}
                <td className="px-4 py-3 text-right flex justify-end gap-2">
                  <button onClick={() => openEdit(it)} className="p-1.5 rounded hover:bg-slate-100" data-testid={`edit-${it.id}`}><Edit3 className="h-4 w-4 text-brand-purple" /></button>
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`del-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={cols.length + 1} className="px-4 py-10 text-center text-slate-400">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" data-testid="crud-modal">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-xl font-bold">{editing ? "Edit" : "Create"} {tab.label}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {fields.map((f) => (
                <div key={f.name} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <label className="text-xs font-semibold text-slate-600">{f.label}{f.required && " *"}</label>
                  {f.type === "textarea" ? (
                    <textarea rows="3" required={f.required} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
                  ) : f.type === "checkbox" ? (
                    <div><label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })} data-testid={`field-${f.name}`} /> Enable</label></div>
                  ) : (
                    <input type={f.type === "number" ? "number" : "text"} required={f.required} placeholder={f.placeholder} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`field-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" onClick={() => setOpen(false)} className="btn-outline !py-2 !px-4 text-sm">Cancel</button>
              <button className="btn-primary !py-2 !px-4 text-sm" data-testid="crud-save">{editing ? "Save changes" : "Create"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ReadOnlyList({ endpoint, columns, allowStatus = false, testKey = "req" }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [endpoint]);
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`${endpoint}/${id}`); load();
  };
  const setStatus = async (id, status) => {
    await api.put(`${endpoint}/${id}?status=${status}`); load();
  };
  return (
    <div data-testid={`list-${testKey}`}>
      <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>{columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-slate-100" data-testid={`row-${testKey}-${it.id}`}>
                {columns.map((c) => <td key={c.key} className="px-4 py-3">{c.render ? c.render(it) : String(it[c.key] ?? "—").slice(0, 100)}</td>)}
                <td className="px-4 py-3 text-right">
                  {allowStatus && (
                    <select defaultValue={it.status || "pending"} onChange={(e) => setStatus(it.id, e.target.value)} className="!py-1 !px-2 text-xs inline-block w-auto mr-2" data-testid={`status-${it.id}`}>
                      <option value="pending">Pending</option><option value="confirmed">Confirmed</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
                    </select>
                  )}
                  <button onClick={() => remove(it.id)} className="p-1.5 rounded hover:bg-red-50" data-testid={`del-${it.id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-4 py-10 text-center text-slate-400">No records yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const FIELDS = [
    { name: "hospital_name", label: "Hospital name" },
    { name: "tagline", label: "Tagline" },
    { name: "address", label: "Address", type: "textarea" },
    { name: "phone", label: "General phone" },
    { name: "emergency_phone", label: "Emergency phone (24x7)" },
    { name: "whatsapp", label: "WhatsApp number (with country code, e.g. +919876543210)" },
    { name: "email", label: "Contact email" },
    { name: "map_embed", label: "Google Maps embed (paste full <iframe…> code from Google Maps → Share → Embed a map)", type: "textarea" },
    { name: "ga_measurement_id", label: "Google Analytics Measurement ID (e.g. G-XXXXXXX)" },
    { name: "facebook_url", label: "Facebook URL" },
    { name: "instagram_url", label: "Instagram URL" },
    { name: "twitter_url", label: "Twitter / X URL" },
    { name: "youtube_url", label: "YouTube URL" },
    { name: "linkedin_url", label: "LinkedIn URL" },
  ];
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const f = {};
      FIELDS.forEach((x) => { f[x.name] = r.data?.[x.name] ?? ""; });
      setForm(f);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Site settings updated. Changes are live across the website.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-500">Loading settings…</div>;

  return (
    <div data-testid="settings-panel">
      <h2 className="text-2xl font-bold mb-1">Site Settings</h2>
      <p className="text-sm text-slate-500 mb-6">Update hospital contact details, social URLs, Google Maps embed and Google Analytics ID. Changes apply across the whole website immediately.</p>
      <form onSubmit={save} className="bg-white rounded-2xl border border-slate-100 p-6 grid md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.type === "textarea" ? "md:col-span-2" : ""}>
            <label className="text-xs font-semibold text-slate-600">{f.label}</label>
            {f.type === "textarea" ? (
              <textarea rows="3" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`settings-${f.name}`} />
            ) : (
              <input type="text" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`settings-${f.name}`} />
            )}
          </div>
        ))}
        <div className="md:col-span-2 flex justify-end">
          <button disabled={saving} className="btn-primary !py-2 !px-5 text-sm" data-testid="settings-save">{saving ? "Saving…" : "Save Settings"}</button>
        </div>
      </form>
    </div>
  );
}

function Overview() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const endpoints = ["/doctors", "/specialties", "/services", "/packages", "/articles", "/testimonials"];
    const protectedEps = ["/appointments", "/second-opinions", "/career-applications", "/contact-messages"];
    Promise.all([
      ...endpoints.map((e) => api.get(e).then((r) => [e, r.data.length]).catch(() => [e, 0])),
      ...protectedEps.map((e) => api.get(e).then((r) => [e, r.data.length]).catch(() => [e, 0])),
    ]).then((res) => setCounts(Object.fromEntries(res)));
  }, []);
  const cards = [
    { k: "/appointments", l: "Pending Appointments", c: "bg-brand-purple text-white" },
    { k: "/second-opinions", l: "Second Opinion Requests", c: "bg-brand-green text-white" },
    { k: "/career-applications", l: "Career Applications", c: "bg-slate-900 text-white" },
    { k: "/contact-messages", l: "Contact Messages", c: "bg-amber-500 text-white" },
    { k: "/doctors", l: "Doctors", c: "bg-white border border-slate-100" },
    { k: "/specialties", l: "Specialties", c: "bg-white border border-slate-100" },
    { k: "/services", l: "Services", c: "bg-white border border-slate-100" },
    { k: "/packages", l: "Packages", c: "bg-white border border-slate-100" },
  ];
  return (
    <div data-testid="admin-overview">
      <h2 className="text-2xl font-bold mb-5">Overview</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.k} className={`${c.c} rounded-2xl p-5`}>
            <div className="text-xs uppercase opacity-80">{c.l}</div>
            <div className="text-3xl font-bold mt-2">{counts[c.k] ?? "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [active, setActive] = useState("overview");
  const tab = TABS.find((t) => t.key === active);

  return (
    <div className="theme-light min-h-screen bg-slate-50 flex" data-testid="admin-dashboard">
      <aside className="w-64 bg-white border-r border-slate-100 min-h-screen sticky top-0 hidden md:block">
        <Link to="/" className="flex items-center gap-2 p-5 border-b border-slate-100">
          <img src={LOGO_URL} alt="" className="h-9 w-9 object-contain" />
          <div className="font-bold"><span className="text-brand-purple">One</span> <span className="text-brand-green">Hospitals</span></div>
        </Link>
        <nav className="p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setActive(t.key)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${active === t.key ? "bg-brand-purple text-white" : "text-slate-700 hover:bg-slate-50"}`}
              data-testid={`tab-${t.key}`}>
              {t.icon ? <t.icon className="h-4 w-4" /> : <span className="h-4 w-4 inline-block" />} {t.label}
            </button>
          ))}
        </nav>
      </aside>

      <main className="flex-1 p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="text-xs text-slate-500">Logged in as</div>
            <div className="font-semibold">{user?.email}</div>
          </div>
          <div className="flex items-center gap-3">
            <select value={active} onChange={(e) => setActive(e.target.value)} className="md:hidden !py-2 !px-3 text-sm w-auto inline-block" data-testid="mobile-tab-select">
              {TABS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <button onClick={async () => { await logout(); nav("/admin/login"); }} className="btn-outline !py-2 !px-4 text-sm" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>

        {active === "overview" && <Overview />}
        {active === "site-settings" && <SettingsPanel />}
        {active === "appointments" && (
          <div>
            <h2 className="text-2xl font-bold mb-5">Appointments</h2>
            <ReadOnlyList endpoint="/appointments" allowStatus testKey="appointments" columns={[
              { key: "name", label: "Patient" }, { key: "mobile", label: "Mobile" }, { key: "department", label: "Department" },
              { key: "preferred_date", label: "Date" }, { key: "preferred_time", label: "Time" }, { key: "status", label: "Status" }]} />
          </div>
        )}
        {active === "second-opinions" && (
          <div>
            <h2 className="text-2xl font-bold mb-5">Second Opinion Requests</h2>
            <ReadOnlyList endpoint="/second-opinions" testKey="second-opinions" columns={[
              { key: "name", label: "Patient" }, { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
              { key: "medical_history", label: "History" },
              { key: "files", label: "Files", render: (r) => (r.file_paths || []).length },
              { key: "created_at", label: "Submitted" }]} />
          </div>
        )}
        {active === "career-applications" && (
          <div>
            <h2 className="text-2xl font-bold mb-5">Career Applications</h2>
            <ReadOnlyList endpoint="/career-applications" testKey="career-applications" columns={[
              { key: "name", label: "Name" }, { key: "position", label: "Position" }, { key: "email", label: "Email" },
              { key: "mobile", label: "Mobile" }, { key: "created_at", label: "Date" }]} />
          </div>
        )}
        {active === "contact-messages" && (
          <div>
            <h2 className="text-2xl font-bold mb-5">Contact Messages</h2>
            <ReadOnlyList endpoint="/contact-messages" testKey="contact-messages" columns={[
              { key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "mobile", label: "Mobile" },
              { key: "subject", label: "Subject" }, { key: "message", label: "Message" }]} />
          </div>
        )}
        {tab?.fields && <CrudPanel tab={tab} />}
      </main>
    </div>
  );
}
